// eslint-disable-next-line import/prefer-default-export

// Tracking parameters to capture and persist for cross-session ad attribution
export const AD_TRACKING_PARAMS = [
  'msclkid',   // Microsoft (Bing) Ads click ID
  'gclid',     // Google Ads click ID
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

const AD_TRACKING_STORAGE_KEY = 'avenue_ad_tracking';
const AD_TRACKING_EXPIRY_DAYS = 30; // Standard attribution window

/**
 * Persists ad tracking params (msclkid, gclid, UTMs, etc.) to localStorage
 * with a 30-day expiry so that cross-session attribution is maintained.
 * Called automatically via gatsby-browser.js onRouteUpdate.
 */
export function captureAndStoreTrackingParams() {
  if (typeof window === 'undefined') return;

  const searchParams = new URLSearchParams(window.location.search);
  const hasTracking = AD_TRACKING_PARAMS.some((p) => searchParams.has(p));

  if (!hasTracking) return;

  const params = {};
  AD_TRACKING_PARAMS.forEach((p) => {
    if (searchParams.has(p)) {
      params[p] = searchParams.get(p);
    }
  });

  const expiry = Date.now() + AD_TRACKING_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  try {
    localStorage.setItem(AD_TRACKING_STORAGE_KEY, JSON.stringify({ params, expiry }));
  } catch (e) {
    // localStorage unavailable (e.g. private mode quota exceeded) — silently ignore
  }
}

/**
 * Retrieves previously stored ad tracking params from localStorage.
 * Returns an empty object if none exist or if the stored data has expired.
 */
export function getStoredTrackingParams() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(AD_TRACKING_STORAGE_KEY);
    if (!raw) return {};
    const { params, expiry } = JSON.parse(raw);
    if (!Number.isFinite(expiry) || Date.now() > expiry) {
      localStorage.removeItem(AD_TRACKING_STORAGE_KEY);
      return {};
    }
    if (!params || typeof params !== 'object' || Array.isArray(params)) {
      return {};
    }
    const filteredParams = {};
    AD_TRACKING_PARAMS.forEach((param) => {
      if (Object.prototype.hasOwnProperty.call(params, param) && params[param] != null) {
        filteredParams[param] = String(params[param]);
      }
    });
    return filteredParams;
  } catch (e) {
    try {
      localStorage.removeItem(AD_TRACKING_STORAGE_KEY);
    } catch (storageError) {
      // localStorage unavailable — silently ignore
    }
    return {};
  }
}

export function sanitiseString(htmlString = '') {
  if (typeof document === 'undefined') {
    return `${htmlString}`;
  }
  const el = document.createElement('div');
  el.innerHTML = htmlString;
  return el.textContent;
}

export function getParameters(urlString = '') {
  const ret = {};
  const cleanedUrl = sanitiseString(`${urlString}`);
  const paramString = cleanedUrl.split('?')[1];

  if (!paramString) {
    return ret;
  }

  const paramsArray = paramString.split('&');
  const paramsArrayLength = paramsArray.length;

  for (let i = 0; i < paramsArrayLength; i += 1) {
    const pair = paramsArray[i].split('=');
    // eslint-disable-next-line prefer-destructuring
    ret[pair[0]] = pair[1];
  }

  return ret;
}

export function isBrowser() {
  return typeof window !== 'undefined' && window.location;
}

/**
 * Appends current page URL parameters to a destination URL.
 * Also merges any ad tracking params (msclkid, UTMs, etc.) that were captured
 * and stored in localStorage during a previous session so that cross-session
 * ad attribution (e.g. Bing/Google Ads) is maintained.
 *
 * Priority order (highest → lowest):
 *   1. Params already present in destinationUrl  (never overridden)
 *   2. Params in the current page URL            (live ad click)
 *   3. Params stored in localStorage             (prior session / previous visit)
 *
 * @param {string} destinationUrl - The URL to append parameters to
 * @param {string[]} [paramsToPreserve] - Optional array of specific param names to preserve. If not provided, all params are preserved.
 * @returns {string} - The destination URL with preserved parameters
 */
export function appendCurrentUrlParams(destinationUrl, paramsToPreserve = null) {
  if (!isBrowser() || !destinationUrl) {
    return destinationUrl || '';
  }

  const currentParams = new URLSearchParams(window.location.search);

  // Build a merged param map: stored (lowest priority) + current URL (higher priority)
  const storedParams = getStoredTrackingParams();
  const mergedParams = new URLSearchParams();

  // First add stored tracking params (lowest priority source)
  Object.entries(storedParams).forEach(([key, value]) => {
    if (!paramsToPreserve || paramsToPreserve.includes(key)) {
      mergedParams.set(key, value);
    }
  });

  // Then overlay with current URL params (overrides stored values for the same key)
  currentParams.forEach((value, key) => {
    if (!paramsToPreserve || paramsToPreserve.includes(key)) {
      mergedParams.set(key, value);
    }
  });

  if (mergedParams.toString() === '') {
    return destinationUrl;
  }

  try {
    const url = new URL(destinationUrl);

    mergedParams.forEach((value, key) => {
      // Don't override if the destination URL already has this param
      if (!url.searchParams.has(key)) {
        url.searchParams.append(key, value);
      }
    });

    return url.toString();
  } catch (e) {
    // If URL parsing fails, fall back to simple string concatenation
    const separator = destinationUrl.includes('?') ? '&' : '?';
    const paramsToAppend = Array.from(mergedParams.entries())
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

    return paramsToAppend ? `${destinationUrl}${separator}${paramsToAppend}` : destinationUrl;
  }
}

